import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styles from './UpdatePnl.module.scss';
import { RestS } from '../../../rest/rest-service';
import { LendingStatus, PnlFsNames, UnderwritingSteps, pnlFormat } from '../../../interfaces/interfaces';
import { getLocalUser, isNumeric } from '../../../components/functions/utilities';
import { Field, Form, FormSpy } from 'react-final-form';
import CircularLoader from '../../../components/common/CircularLoader/CircularLoader';
import { RiErrorWarningFill as ExlamationIcon } from 'react-icons/ri'
import PnlTable from './PnlTable';
import { useAppDispatch, useAppSelector } from '../../../redux/reduxHooks';
import { setRefreshFinanceStatement } from '../../../redux/financeStatement/financeStatement';
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import moment from 'moment';
import MeritoCheckbox from '../../Finance/components/CustomCheckbox/MeritoCheckbox';
import { HiChevronRight as RightChevron } from 'react-icons/hi';
import { UnderwritingFormContext } from '../context/UnderwritingFormContext';

const metricsToShow = new Set([
    PnlFsNames.netSales,
    PnlFsNames.cogs,
    PnlFsNames.grossProfit,
    PnlFsNames.cm1Perc,
    PnlFsNames.adSpend,
    PnlFsNames.cm2Perc,
    PnlFsNames.ebitdaMargin,
    PnlFsNames.netCashMargin
]);

const checkForMissing = new Set([
    PnlFsNames.shippingCost,
    PnlFsNames.packagingCost,
    PnlFsNames.platformCommission,
    PnlFsNames.operatingExpenses,
    PnlFsNames.loanServicing
])


const percentageColumns = ["Monthly Growth %", "Gross Profit %", "CM1 %", "CM2 %", "EBITDA Margin", "Net Cash Margin"];

const metricsConfig = {
    [PnlFsNames.cogs]: {
        dependentMetrics: new Set(),
        isDummyRow: false,
        isBorder: true,
        isBold: false,
    },
    [PnlFsNames.grossProfit]: {
        dependentMetrics: new Set(),
        isDummyRow: false,
        isBorder: true,
        isBold: true,
    },
    [PnlFsNames.cm1Perc]: {
        dependentMetrics: new Set([PnlFsNames.shippingCost, PnlFsNames.packagingCost]),
        isDummyRow: false,
        isBorder: true,
        isBold: true,
    },
    [PnlFsNames.adSpend]: {
        dependentMetrics: new Set(),
        isDummyRow: true,
        isBorder: true,
        isBold: false,
    },
    [PnlFsNames.cm2Perc]: {
        dependentMetrics: new Set([PnlFsNames.shippingCost, PnlFsNames.packagingCost, PnlFsNames.platformCommission]),
        isDummyRow: false,
        isBorder: true,
        isBold: true,
    },
    [PnlFsNames.ebitdaMargin]: {
        dependentMetrics: new Set([PnlFsNames.shippingCost, PnlFsNames.packagingCost, PnlFsNames.platformCommission, PnlFsNames.operatingExpenses]),
        isDummyRow: true,
        isBorder: true,
        isBold: true,
    },
    [PnlFsNames.netCashMargin]: {
        dependentMetrics: new Set([PnlFsNames.shippingCost, PnlFsNames.packagingCost, PnlFsNames.platformCommission, PnlFsNames.operatingExpenses, PnlFsNames.loanServicing]),
        isDummyRow: false,
        isBorder: false,
        isBold: true,
    }

}

type Props = {
    setFormPristine: (value: boolean) => void,
    handlePnlSubmit: (values: any) => void,
}

const UpdatePnl = forwardRef(({ setFormPristine, handlePnlSubmit }: Props, ref) => {
    const [financeFormData, setFinanceFormData] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const currentUser = getLocalUser();
    const navigate = useNavigate();
    const [financeCategories, setFinanceCategories] = useState({});
    const [isOutstandingLoanAmt, setIsOutstandingLoanAmt] = useState(false);
    const { formData, setFormData, setStepValidity, formStepsValidity, handleAndSetFormStatuses } = useContext(UnderwritingFormContext);

    const formRef = useRef(null);
    const [submitLoading, setSubmitLoading] = useState(false);

    const fetchFinanceData = async () => {
        try {
            const data: any = await RestS.getFinanceStatementTable(true);
            // console.log(data);
            if (data?.tableData) {
                const { months, tableData } = data;
                const formInitialData = formatFinanceDataForForm(tableData, months);
                setFinanceFormData(formInitialData)
            }
        } catch (error) {
            console.log('error', error);
        }
    }

    const fetchTabsData = async () => {
        try {
            const data: any = await RestS.getFinanceTabsData();
            // console.log('tabsData', data)
            // setFinanceCategories(data?.financeCategories)
            if (data?.financeCategories) {
                const financeCategoriesObj = data?.financeCategories.reduce((prev, curVal) => {
                    prev[curVal.name] = {
                        ...curVal,
                    }
                    return prev;
                }, {});
                setFinanceCategories(financeCategoriesObj);
            }
            if (data?.loanData && data?.loanData?.length) {
                const loanData = data?.loanData;
                const outstandingFsIds = loanData?.reduce((prev, curVal) => {
                    if (!curVal.outstandingAmount && curVal.outstandingAmount != 0) {
                        prev.push(curVal.metricId);
                        return prev;
                    } else {
                        const enteredOn = moment(curVal?.outstandingAmountEnteredOn, "MM-YYYY");
                        const currentMonth = moment();
                        // console.log('enteredOn', enteredOn.toDate(), !enteredOn.isSame(currentMonth, "month"))
                        const endDate = moment(curVal?.endDate, "MM-YYYY");
                        if (endDate?.isAfter(currentMonth, "month")) {
                            if (!enteredOn.isSame(currentMonth, "month")) {
                                prev.push(curVal.metricId)
                            }
                        }
                        return prev;
                    }
                }, []);
                if (outstandingFsIds.length) {
                    setIsOutstandingLoanAmt(true);
                } else {
                    if (isOutstandingLoanAmt) {
                        setIsOutstandingLoanAmt(false);
                    }
                }
            }

        } catch (error) {
            console.log('error', error);
        }
    }

    const formatFinanceDataForForm = (tableData: any[], months?: string[]) => {
        // const
        let totalCount = 0;
        let countInLakhs = 0;
        let countInThousands = 0;
        const missingColumns = new Set();
        const formattedBodyData = tableData.reduce((prev, curVal) => {
            const { name: metricName } = curVal;
            const isAvgColumns = percentageColumns.includes(metricName) ? true : false;
            let totalRows = 0;
            let totalOfRows = 0;
            let rowIsAllNull = true;
            let showRedBanner = false;
            if (checkForMissing.has(metricName) || metricsToShow.has(metricName)) {
                let rowValuesObj = {};
                if (curVal.rowValues.length) {
                    rowValuesObj = curVal.rowValues.reduce((rowValuesPrev, rowValuesCurVal) => {
                        rowValuesPrev[rowValuesCurVal.monthYear] = {
                            ...rowValuesCurVal,
                        }
                        return rowValuesPrev;
                    }, {});
                }
                months.forEach((item, index) => {
                    if (rowValuesObj[item] != undefined) {
                        const rowNumberValue = isNumeric(`${rowValuesObj[item]['value']}`) ? Number(rowValuesObj[item]['value']) : 0;
                        totalOfRows += rowNumberValue;
                        if (rowNumberValue) {
                            rowIsAllNull = false;
                            totalRows += 1;
                        }
                        if (rowNumberValue >= 10000 && !isAvgColumns) {
                            countInLakhs += 1;
                        }
                        if (rowNumberValue < 10000 && !isAvgColumns) {
                            countInThousands += 1
                        }
                        if (!isAvgColumns) {
                            totalCount += 1;
                        }
                    }
                });
                if (rowIsAllNull && checkForMissing.has(metricName)) {
                    missingColumns.add(metricName);
                }
                if (metricsToShow.has(metricName)) {
                    if (metricsConfig[metricName] != undefined) {

                        // console.log(metricsConfig[metricName])
                        const dependentMetrics = Array.from(metricsConfig[metricName]['dependentMetrics']);
                        for (const metric of dependentMetrics) {
                            if (missingColumns.has(metric)) {
                                showRedBanner = true;
                                break;
                            }
                        }
                    }
                    const metricConfig = metricsConfig[metricName] ?? {
                        isDummyRow: false,
                        isBorder: false,
                        isBold: false,
                    };
                    // console.log('metricConfig', metricConfig);
                    // console.log('metricName', metricName, totalOfRows, totalRows)
                    prev.push({
                        metricName,
                        total: isAvgColumns ? totalRows == 0 ? 0 : (Number(totalOfRows / totalRows)).toFixed(1) : totalOfRows,
                        isPercentage: isAvgColumns,
                        showRedBanner,
                        rowIsAllNull,
                        isDummyRow: metricsConfig[metricName] ? metricsConfig[metricName]['isDummyRow'] : false,
                        ...metricConfig,
                    })

                }
            }
            return prev;
        }, []);

        const countinLakhsPercentage = (countInLakhs / totalCount) * 100;
        const unitFormat = countinLakhsPercentage > 20 ? pnlFormat.lakhs : pnlFormat.thousands;

        return {
            pnlData: formattedBodyData,
            missingCosts: missingColumns,
            unitFormat
        }
    }

    const { refreshFinanceStatement } = useAppSelector((state) => state.financeStatement);
    const dispatch = useAppDispatch();

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([fetchFinanceData(), fetchTabsData()])
            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (refreshFinanceStatement) {
            fetchFinanceData();
            fetchTabsData();
            dispatch(setRefreshFinanceStatement(false));
        }
    }, [refreshFinanceStatement]);

    const formatRedirectLink = useMemo(() => {
        if (financeFormData && financeFormData?.missingCosts && financeFormData?.missingCosts.size) {
            const firstElement: any = Array.from(financeFormData?.missingCosts)[0];
            // return `/`
            if (financeCategories[firstElement] != undefined) {
                const category = financeCategories[firstElement].feTab.replace(/\s+/g, '-').toLowerCase();
                return `/pnltracker/edit/${category}?section=${firstElement}`;
            }
        }
    }, [financeCategories, financeFormData]);


    const isOnlyLoansMissing = useMemo(() => {
        if (financeFormData && financeFormData?.missingCosts && financeFormData?.missingCosts.size == 1) {
            const firstElement: any = Array.from(financeFormData?.missingCosts)[0];
            return firstElement == PnlFsNames.loanServicing;
        }
    }, [financeFormData])

    useImperativeHandle(ref, () => ({
        submitForm: () => {
            return new Promise((resolve) => {
                formRef.current.submit();
                console.log("pristing", formRef?.current)
                if (!formStepsValidity[UnderwritingSteps.pnl]) {
                    resolve({ success: false, values: null, pristine: formRef?.current?.getState().pristine })
                } else {
                    resolve({ success: true, values: formRef?.current?.getState().values, pristine: formRef?.current?.getState().pristine })
                }
            })
        }
    }), [formStepsValidity]);


    const handleInFormSubmit = async (values) => {
        setSubmitLoading(true);
        let submitFormData = {
            data: {
                ...formData,
                isOutstandingLoanAmt: values['isConfirmNoLoan'] ?? false,
                missingCosts: Array.from(values['missingCosts']) ?? []
            },
            step: UnderwritingSteps.pnl,
            applicationStatus: LendingStatus.PENDING_APPROVAL
        }
        handlePnlSubmit(submitFormData);
    }




    return (
        <div className={styles.rootContainer}>
            {loading ?
                <div className={styles.loadingContainer}>
                    <CircularLoader />
                </div>
                : <Form
                    initialValues={{
                        ...financeFormData,
                        isOnlyLoansMissing,
                        isOutstandingLoanAmt: isOutstandingLoanAmt
                    }}
                    onSubmit={(values) => {
                        console.log('values', values);
                        return {};
                    }}
                    validate={(values) => {
                        let errors = {};
                        if (values?.missingCosts && values?.missingCosts.size && !values?.isOnlyLoansMissing) {
                            errors['missingCosts'] = 'Please fill all the missing costs.'
                        } else if (values?.isOutstandingLoanAmt) {
                            errors['isOutstandingLoanAmt'] = "Please fill outstanding amount"
                        }
                        return errors;
                    }}
                    render={({ values, handleSubmit, hasValidationErrors, form }) => {
                        formRef.current = form;
                        return (
                            <form className={styles.formWrapper} onSubmit={handleSubmit}>
                                <FormSpy subscription={{ valid: true, pristine: true }} onChange={(state) => {
                                    // console.log('formState', state)
                                    // formStepsValidityRef.current[UnderwritingSteps.company] = !state.valid;
                                    setFormPristine(state.pristine)
                                    setStepValidity(UnderwritingSteps.pnl, state.valid)
                                }} />
                                <div className={styles.formRootContainer}>
                                    <div className={styles.leftContainer}>
                                        <div className={styles.title}>
                                            {`${currentUser.companyName}'s`} <br />
                                            P&L Report
                                        </div>
                                        <div className={styles.subText}>
                                            This statement was generated using our <span><a href="/pnltracker/statement" target='_blank'>P&L Tracker</a></span>  feature.
                                        </div>
                                        {values?.missingCosts && values?.missingCosts.size && !isOnlyLoansMissing ?
                                            <div className={styles.missingRootContainer}>
                                                <div className={styles.warningContainer}>
                                                    <div className={styles.iconContainer}>
                                                        <ExlamationIcon />
                                                    </div>
                                                    <div className={styles.missingCostList}>
                                                        <div className={styles.listTitle}>
                                                            To proceed, please add:
                                                        </div>
                                                        <ul>
                                                            {Array.from(values?.missingCosts).map((item, index) => {
                                                                return (
                                                                    <li key={index}>
                                                                        {item == PnlFsNames.loanServicing ? "Loans" : item}
                                                                    </li>
                                                                )
                                                            })}
                                                        </ul>
                                                    </div>
                                                </div>

                                                <div className={styles.btnRootContainer}>
                                                    <div className={styles.btnContainer}>
                                                        <Link to={formatRedirectLink} target='_blank'>
                                                            <MeritoButton
                                                                btnText="Update Now"
                                                                btnType='button'
                                                                type='primary-1'
                                                                size='md2'
                                                            />
                                                        </Link>

                                                    </div>
                                                </div>
                                            </div>
                                            : isOnlyLoansMissing ?
                                                <div className={styles.missingRootContainer}>
                                                    <div className={styles.warningContainer}>
                                                        <div className={styles.iconContainer}>
                                                            <ExlamationIcon />
                                                        </div>
                                                        <div className={styles.missingCostList}>
                                                            <div className={`${styles.listTitle} ${styles.center}`}>
                                                                Your loan information is missing.
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <Field validate={(values) => {
                                                        if (!values) {
                                                            return `Please confirm`
                                                        }
                                                    }} name='isConfirmNoLoans'>
                                                        {(props) => {
                                                            return (
                                                                <div className={styles.checkboxFieldContainer}>

                                                                    <MeritoCheckbox
                                                                        name={props.input.name}
                                                                        error={props.meta.error}
                                                                        touched={props.meta.touched}
                                                                        value={props.input.value}
                                                                        onChange={props.input.onChange}
                                                                        showError={false}
                                                                    />
                                                                    <div className={styles.checkboxLabel}>
                                                                        We confirm there are no outstanding loans.
                                                                    </div>
                                                                </div>
                                                            )
                                                        }}
                                                    </Field>

                                                    <div className={styles.btnRootContainer}>

                                                        <div className={styles.btnContainer}>
                                                            {!values?.isConfirmNoLoans ?
                                                                <Link to={'/pnltracker/edit/other-expenses?section=Loan Servicing'} target='_blank'>
                                                                    <MeritoButton
                                                                        btnText="Add now"
                                                                        btnType='button'
                                                                        type='secondary-2'
                                                                        size='md2'
                                                                    />
                                                                </Link>
                                                                :
                                                                <MeritoButton
                                                                    btnText="Submit"
                                                                    btnType='button'
                                                                    type='primary-1'
                                                                    size='md2'
                                                                    icon={<RightChevron />}
                                                                    isIconToRight={true}
                                                                    disabled={hasValidationErrors}
                                                                    onClick={() => {
                                                                        const formValues = handleSubmit();
                                                                        if (!hasValidationErrors) {
                                                                            handleInFormSubmit(values);
                                                                        }
                                                                    }}
                                                                />

                                                            }

                                                        </div>
                                                    </div>
                                                </div>

                                                : isOutstandingLoanAmt ?

                                                    <div className={styles.missingRootContainer}>
                                                        <div className={styles.warningContainer}>
                                                            <div className={styles.iconContainer}>
                                                                <ExlamationIcon />
                                                            </div>
                                                            <div className={styles.missingCostList}>
                                                                <div className={`${styles.listTitle} ${styles.center}`}>
                                                                    Outstanding principal amount is missing for some loans.
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className={styles.btnRootContainer}>
                                                            <div className={styles.btnContainer}>
                                                                <Link to={'/pnltracker/edit/other-expenses?section=Loan Servicing'} target='_blank'>
                                                                    <MeritoButton
                                                                        btnText="Update Now"
                                                                        btnType='button'
                                                                        type='secondary-2'
                                                                        size='md2'
                                                                    />
                                                                </Link>

                                                            </div>
                                                        </div>
                                                    </div>



                                                    :

                                                    <div className={styles.missingRootContainer}>
                                                        <div className={styles.btnRootContainer}>
                                                            <Link to={'/pnltracker/edit/sales-channels'} target='_blank'>
                                                                <MeritoButton
                                                                    btnText="Update P&L"
                                                                    btnType='button'
                                                                    type='secondary-2'
                                                                    size='md2'
                                                                />
                                                            </Link>
                                                            <MeritoButton
                                                                btnText="Submit"
                                                                btnType='submit'
                                                                type='primary-1'
                                                                size='md2'
                                                                icon={<RightChevron />}
                                                                isIconToRight={true}
                                                                disabled={hasValidationErrors}
                                                                onClick={() => {
                                                                    const formValues = handleSubmit();
                                                                    // console.log('values', values)
                                                                    if (!hasValidationErrors) {
                                                                        handleInFormSubmit(values);
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                        }


                                    </div>

                                    <div className={styles.rightContainer}>
                                        <PnlTable />
                                    </div>
                                </div>
                            </form>
                        )
                    }}
                >

                </Form>}
        </div>
    );
})

export default UpdatePnl;